import type { MaintenanceCandidate } from '@course-authoring/shared-types';
import { checkCurrentAuthoringBuild } from './currentAuthoringBuild';
import { guardedMaintenanceTransport, prepareMaintenanceLesson, publishMaintenanceItem, refreshMaintenanceItem } from './maintenanceRepublishApi';
import { isFatalPublishingError, type DeployAccountResult } from './courseDeployApi';
import { runPublishDelivery } from './publishDelivery';

export const maintenanceItemKey = (item: Pick<MaintenanceCandidate, 'kind' | 'id'>): string => `${item.kind}:${item.id}`;
export interface MaintenanceOutcome {
  item: MaintenanceCandidate;
  status: 'pending' | 'running' | 'completed' | 'skipped' | 'failed';
  message?: string;
  results?: DeployAccountResult[];
  targetCount?: number;
  publicationMs?: number;
  deliveryMs?: number;
  totalMs?: number;
}
export function orderMaintenanceItems(items: MaintenanceCandidate[]): MaintenanceCandidate[] {
  return [...new Map(items.map(item => [maintenanceItemKey(item), item])).values()].sort((a, b) =>
    (a.kind === b.kind ? 0 : a.kind === 'lesson' ? -1 : 1)
    || (a.lastPublishedAt ? Date.parse(a.lastPublishedAt) : Infinity) - (b.lastPublishedAt ? Date.parse(b.lastPublishedAt) : Infinity));
}

/** Intentionally lives only in this tab. No storage, retry timer or resume intent. */
export async function runMaintenanceBatch(items: MaintenanceCandidate[], exportedBy: string, options: {
  onOutcome: (outcome: MaintenanceOutcome) => void;
  onProgress: (message: string) => void;
  onItemSettled: () => Promise<unknown>;
  signal: AbortSignal;
  retry?: boolean;
}): Promise<void> {
  let stopReason: string | undefined;
  for (let item of orderMaintenanceItems(items)) {
    if (options.signal.aborted) return;
    if (stopReason || item.run?.state === 'running') {
      options.onOutcome({ item, status: 'skipped', message: stopReason ?? 'A recorded delivery is still in progress.' });
      continue;
    }
    const startedAt = performance.now();
    let publicationMs: number | undefined;
    let targetCount = 0;
    const deliveryResults: DeployAccountResult[] = [];
    try {
      const build = await checkCurrentAuthoringBuild();
      if (build.status !== 'current') {
        stopReason = 'The current browser build could not be confirmed. Reload before starting another batch.';
        options.onOutcome({ item, status: 'skipped', message: stopReason });
        continue;
      }
      if (options.signal.aborted) return;
      options.onOutcome({ item, status: 'running' });
      options.onProgress(`Republishing ${item.title}…`);
      if (options.retry) item = await refreshMaintenanceItem(item);
      const publication = await publishMaintenanceItem(item);
      publicationMs = performance.now() - startedAt;
      await runPublishDelivery({
        contentKind: item.kind, packageId: item.id, exportedBy, contentPublishedAt: publication.publishedAt,
        loadContext: async () => publication.context,
        deployPackage: guardedMaintenanceTransport(publication.expected),
        prepareLesson: (lessonId, accounts) => prepareMaintenanceLesson(publication.expected, lessonId, accounts),
        onProgress: options.onProgress,
        onResults: results => { deliveryResults.splice(0, deliveryResults.length, ...results); },
        onTargetCount: count => { targetCount = count; },
        onVerifyTargets: () => {}, onSnapshotChanged: () => {}, onRunRecorded: () => {}, onParentSnapshotsChanged: () => {},
      });
      const totalMs = performance.now() - startedAt;
      options.onOutcome({ item, status: deliveryResults.length > 0 && deliveryResults.every(row => row.success) ? 'completed' : 'failed',
        results: deliveryResults, targetCount, publicationMs, deliveryMs: totalMs - publicationMs, totalMs });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Republish failed.';
      if (isFatalPublishingError(error)) stopReason = 'Administrator access was lost. Sign in with an authorized account before retrying.';
      const status = (error as { status?: unknown } | null)?.status;
      options.onOutcome({ item, status: status === 409 && publicationMs === undefined ? 'skipped' : 'failed',
        message: stopReason ?? message, targetCount, publicationMs, totalMs: performance.now() - startedAt });
    }
    if (!options.signal.aborted) await options.onItemSettled();
  }
}
